import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { getLoginUrlForDomain, isValidDomainKey, DOMAIN_REGISTRY } from '../../utils/domain-registry';

type ActionMode = 'verifyEmail' | 'resetPassword' | 'recoverEmail' | null;

interface ActionState {
  isLoading: boolean;
  isSuccess: boolean;
  errorMessage: string | null;
}

@Component({
  selector: 'tandem-action-handler',
  templateUrl: './action-handler.component.html',
  styleUrls: ['./action-handler.component.scss']
})
export class ActionHandlerComponent implements OnInit {
  mode: ActionMode = null;
  oobCode: string | null = null;
  originDomain: string | null = null;
  
  state: ActionState = {
    isLoading: true,
    isSuccess: false,
    errorMessage: null
  };

  newPasswordForm: FormGroup;
  showPassword = false;

  // Default Tandem branding - minimal, no whitelabel
  readonly logoPath = 'assets/svg/logo/icon_blue.svg';
  readonly brandName = 'Tandem';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private fb: FormBuilder
  ) {
    this.mode = this.route.snapshot.queryParamMap.get('mode') as ActionMode;
    this.oobCode = this.route.snapshot.queryParamMap.get('oobCode');
    
    // Get origin domain from query params (passed through from Firebase action URL)
    const origin = this.route.snapshot.queryParamMap.get('origin');
    this.originDomain = origin && isValidDomainKey(origin) ? origin : 'tandem';

    this.newPasswordForm = this.fb.group({
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[A-Z])(?=.*[!@#$%^&*])/)
      ]]
    });
  }

  ngOnInit(): void {
    if (!this.oobCode) {
      this.state = {
        isLoading: false,
        isSuccess: false,
        errorMessage: 'Invalid or missing action code. Please check your email link and try again.'
      };
      return;
    }

    if (this.mode === 'verifyEmail') {
      this.handleVerifyEmail();
    } else if (this.mode === 'resetPassword') {
      // For password reset, we just show the form - no automatic action
      this.state.isLoading = false;
    } else {
      this.state = {
        isLoading: false,
        isSuccess: false,
        errorMessage: 'Unknown action type. Please check your email link and try again.'
      };
    }
  }

  private async handleVerifyEmail(): Promise<void> {
    if (!this.oobCode) return;

    try {
      await this.authService.verifyEmail(this.oobCode);
      this.state = {
        isLoading: false,
        isSuccess: true,
        errorMessage: null
      };
    } catch (error: any) {
      this.state = {
        isLoading: false,
        isSuccess: false,
        errorMessage: this.getErrorMessage(error.code, 'verify')
      };
    }
  }

  async submitNewPassword(): Promise<void> {
    if (this.newPasswordForm.invalid || !this.oobCode) return;

    this.state.isLoading = true;
    const newPassword = this.newPasswordForm.get('newPassword')?.value;

    try {
      await this.authService.resetPassword(this.oobCode, newPassword);
      this.state = {
        isLoading: false,
        isSuccess: true,
        errorMessage: null
      };
    } catch (error: any) {
      this.state = {
        isLoading: false,
        isSuccess: false,
        errorMessage: this.getErrorMessage(error.code, 'reset')
      };
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  goToLogin(): void {
    // Redirect to the origin domain's login page
    const loginUrl = getLoginUrlForDomain(this.originDomain);
    
    // If we're already on the target domain, use router navigation
    if (this.isCurrentDomain(loginUrl)) {
      this.router.navigate(['/auth/login']);
    } else {
      // Otherwise, redirect to the external domain
      window.location.href = loginUrl;
    }
  }

  private isCurrentDomain(url: string): boolean {
    try {
      const targetUrl = new URL(url);
      return targetUrl.hostname === window.location.hostname;
    } catch {
      return false;
    }
  }

  get originDomainName(): string {
    if (this.originDomain && DOMAIN_REGISTRY[this.originDomain]) {
      return DOMAIN_REGISTRY[this.originDomain].name;
    }
    return 'Tandem Finance';
  }

  get passwordControl(): FormControl {
    return this.newPasswordForm.get('newPassword') as FormControl;
  }

  get hasMinLength(): boolean {
    return this.passwordControl.value?.length >= 8;
  }

  get hasUppercase(): boolean {
    return /[A-Z]/.test(this.passwordControl.value || '');
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*]/.test(this.passwordControl.value || '');
  }

  get pageTitle(): string {
    if (this.state.isSuccess) {
      return this.mode === 'verifyEmail' ? 'Email Verified!' : 'Password Reset!';
    }
    if (this.state.errorMessage) {
      return 'Something Went Wrong';
    }
    if (this.mode === 'verifyEmail') {
      return 'Verifying Email...';
    }
    return 'Reset Your Password';
  }

  private getErrorMessage(errorCode: string, action: 'verify' | 'reset'): string {
    switch (errorCode) {
      case 'auth/expired-action-code':
        return action === 'verify'
          ? 'This verification link has expired. Please request a new verification email from the app.'
          : 'This password reset link has expired. Please request a new one from the login page.';
      
      case 'auth/invalid-action-code':
        return action === 'verify'
          ? 'This verification link is invalid or has already been used. If you\'ve already verified your email, you can close this page.'
          : 'This password reset link is invalid or has already been used. Please request a new one from the login page.';
      
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support for assistance.';
      
      case 'auth/user-not-found':
        return 'We couldn\'t find an account associated with this link. The account may have been deleted.';
      
      case 'auth/weak-password':
        return 'Please choose a stronger password. It must be at least 8 characters with one uppercase letter and one special character (!@#$%^&*).';
      
      default:
        return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
    }
  }
}

